/**
 * Canonical Event Taxonomy for Resilience Forge
 *
 * This module defines the event schema and types used for capturing
 * high-value interactions across the application.
 */

// ============================================
// CONSENT SCOPE TYPES
// ============================================

/**
 * Consent scope determines how event data can be shared
 */
export type ConsentScope =
  | 'personal_only'           // Only visible to the user
  | 'share_with_parent'       // Can be shared with parent/guardian
  | 'share_with_counselor'    // Can be shared with school counselor
  | 'institution_aggregate_only'; // Only aggregated data for institution

// ============================================
// ACTOR TYPES
// ============================================

/**
 * Actor type identifies who/what triggered the event
 */
export type ActorType =
  | 'user'          // End user action
  | 'system'        // System-generated event
  | 'ai_coach'      // AI coach interaction
  | 'scheduler';    // Scheduled/automated process

// ============================================
// EVENT NAMES (Canonical Taxonomy)
// ============================================

/**
 * Critical user flow events - these MUST be captured
 */
export type CriticalEventName =
  // Onboarding
  | 'onboarding_started'
  | 'onboarding_step_completed'
  | 'onboarding_completed'
  // Mood & Wellness
  | 'mood_checkin_completed'
  | 'stress_logged'
  // AWU (Activating, Warming Up)
  | 'awu_session_started'
  | 'awu_session_completed'
  // Techniques
  | 'technique_session_started'
  | 'technique_session_completed'
  // Modules
  | 'module_started'
  | 'module_completed'
  | 'module_quiz_completed'
  // AI Coach
  | 'ai_coach_conversation_started'
  | 'ai_coach_message_sent'
  | 'ai_coach_message_received'
  // Crisis & Safety
  | 'crisis_detected'
  | 'crisis_resource_presented'
  | 'crisis_resource_accessed'
  | 'crisis_mode_exited'
  | 'escalation_triggered'
  | 'escalation_acknowledged';

/**
 * Secondary events - valuable but not critical
 */
export type SecondaryEventName =
  // Engagement
  | 'page_viewed'
  | 'feature_accessed'
  // Journal
  | 'journal_entry_created'
  | 'reflection_written'
  // Goals
  | 'goal_created'
  | 'goal_milestone_completed'
  | 'goal_completed'
  // Brain Gym / EF Games
  | 'ef_game_started'
  | 'ef_game_completed'
  // Rewards
  | 'achievement_unlocked'
  | 'shop_item_purchased'
  // Consent
  | 'consent_granted'
  | 'consent_revoked'
  | 'consent_scope_changed'
  // Assessment
  | 'assessment_started'
  | 'assessment_completed'
  // AI Safety
  | 'ai_coach_topic_boundary'
  | 'ai_coach_output_filtered';

export type EventName = CriticalEventName | SecondaryEventName;

// ============================================
// DATA CLASSIFICATION
// ============================================

/**
 * Data classification for field-level access control
 */
export type DataClassification =
  | 'public'      // Can be freely shared
  | 'sensitive'   // Requires consent for sharing
  | 'restricted'; // Never shared in analytics

// ============================================
// EVENT PAYLOAD TYPES
// ============================================

/**
 * Base payload structure all events share
 */
interface BasePayload {
  // Context
  page_path?: string;
  page_title?: string;
  session_id?: string;
}

/**
 * Onboarding event payloads
 */
export interface OnboardingStartedPayload extends BasePayload {
  referral_source?: string;
}

export interface OnboardingStepCompletedPayload extends BasePayload {
  step_id: string;
  step_name: string;
  step_index: number;
}

export interface OnboardingCompletedPayload extends BasePayload {
  user_intent?: string;
  grade_level?: string;
  total_time_ms?: number;
}

/**
 * Mood & Wellness payloads
 */
export interface MoodCheckinPayload extends BasePayload {
  mood: 'stressed' | 'okay' | 'good';
  context?: string; // Optional context (classified as sensitive)
}

export interface StressLoggedPayload extends BasePayload {
  fuel_before: number;
  fuel_after: number;
  symptom_count: number;
  // Note: actual symptoms are sensitive and not included in analytics
}

/**
 * AWU Session payloads
 */
export interface AWUSessionPayload extends BasePayload {
  session_type?: string;
  duration_ms?: number;
  completed_steps?: number;
  total_steps?: number;
}

/**
 * Technique Session payloads
 */
export interface TechniqueSessionStartedPayload extends BasePayload {
  technique_id: string;
  technique_name: string;
  stress_before?: number;
}

export interface TechniqueSessionPayload extends BasePayload {
  technique_id: string;
  technique_name: string;
  duration_ms: number;
  stress_before?: number;
  stress_after?: number;
  improvement?: number;
}

/**
 * Module payloads
 */
export interface ModulePayload extends BasePayload {
  module_id: string;
  module_name: string;
  module_category?: string;
}

export interface ModuleQuizPayload extends BasePayload {
  module_id: string;
  score: number;
  total_questions: number;
  correct_answers: number;
  is_pre_assessment: boolean;
}

/**
 * AI Coach payloads
 */
export interface AICoachPayload extends BasePayload {
  conversation_id: string;
  message_index?: number;
  // Note: message content is NEVER included (restricted)
}

/**
 * Crisis & Safety payloads
 */
export interface CrisisDetectedPayload extends BasePayload {
  detection_method: 'deterministic' | 'ai_secondary';
  severity: 'low' | 'medium' | 'high';
  trigger_source: 'chat' | 'journal' | 'reflection' | 'feedback' | 'other';
  // Note: actual content is NEVER stored (restricted)
}

export interface CrisisResourcePayload extends BasePayload {
  resource_type: 'hotline' | 'text_line' | 'counselor' | 'emergency';
  resource_id: string;
}

export interface EscalationPayload extends BasePayload {
  escalation_id: string;
  severity: 'medium' | 'high';
  escalated_to: 'counselor' | 'admin';
}

/**
 * Journal payloads
 */
export interface JournalEntryPayload extends BasePayload {
  entry_type: 'personal' | 'reflection' | 'vent';
  word_count: number;
  has_prompt: boolean;
  // Note: entry text is NEVER included (restricted)
}

/**
 * Goal payloads
 */
export interface GoalPayload extends BasePayload {
  goal_id: string;
  goal_type: 'academic' | 'skill' | 'wellness' | 'social' | 'personal';
  milestone_count?: number;
}

export interface GoalCompletedPayload extends BasePayload {
  goal_id: string;
}

/**
 * EF Game payloads
 */
export interface EFGamePayload extends BasePayload {
  game_id: string;
  game_name?: string;
  score: number;
  level?: number;
  accuracy?: number;
  reaction_time_ms?: number;
  is_personal_best?: boolean;
}

/**
 * Consent payloads
 */
export interface ConsentPayload extends BasePayload {
  previous_scope?: ConsentScope;
  new_scope: ConsentScope;
  reason?: string;
}

/**
 * AI Safety payloads
 */
export interface AICoachTopicBoundaryPayload extends BasePayload {
  conversation_id: string;
  category: string | null;
  severity: 'high' | 'moderate' | 'low' | 'none';
  action: 'block' | 'redirect' | 'allow';
}

export interface AICoachOutputFilteredPayload extends BasePayload {
  conversation_id: string;
  violations: string[];
}

/**
 * Union of all payload types
 */
export type EventPayload =
  | BasePayload
  | OnboardingStartedPayload
  | OnboardingStepCompletedPayload
  | OnboardingCompletedPayload
  | MoodCheckinPayload
  | StressLoggedPayload
  | AWUSessionPayload
  | TechniqueSessionStartedPayload
  | TechniqueSessionPayload
  | ModulePayload
  | ModuleQuizPayload
  | AICoachPayload
  | CrisisDetectedPayload
  | CrisisResourcePayload
  | EscalationPayload
  | JournalEntryPayload
  | GoalPayload
  | GoalCompletedPayload
  | EFGamePayload
  | ConsentPayload
  | AICoachTopicBoundaryPayload
  | AICoachOutputFilteredPayload;

// ============================================
// CANONICAL EVENT STRUCTURE
// ============================================

/**
 * Event priority levels for queue processing
 */
export type EventPriority = 'critical' | 'high' | 'normal' | 'low';

/**
 * Event sync status
 */
export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'failed' | 'dead_letter';

/**
 * The canonical event structure
 * All events must conform to this schema
 */
export interface CanonicalEvent {
  // Identity
  idempotency_key: string;      // UUID v4 for deduplication
  event_name: EventName;

  // Context
  tenant_id: string;            // Tenant identifier
  actor_type: ActorType;
  user_id?: string;             // Hashed user identifier (when authenticated)

  // Consent & Classification
  consent_scope: ConsentScope;

  // Timing
  occurred_at: string;          // ISO 8601 timestamp when event occurred
  queued_at: string;            // ISO 8601 timestamp when event was queued

  // Data
  payload: EventPayload;

  // Metadata (local-only, not synced)
  _local?: {
    sync_status: SyncStatus;
    sync_attempts: number;
    last_sync_attempt?: string;
    last_sync_error?: string;
  };
}

/**
 * Event input - what callers provide (some fields are auto-populated)
 */
export interface EventInput {
  event_name: EventName;
  payload: EventPayload;
  consent_scope?: ConsentScope;  // Defaults to current user consent
  actor_type?: ActorType;        // Defaults to 'user'
}

// ============================================
// VALIDATION TYPES
// ============================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// ============================================
// CRITICAL EVENTS LIST
// ============================================

/**
 * Events that must NEVER be dropped, even under quota pressure
 */
export const CRITICAL_EVENTS: Set<EventName> = new Set([
  // Safety events
  'crisis_detected',
  'crisis_resource_presented',
  'crisis_resource_accessed',
  'escalation_triggered',
  'escalation_acknowledged',
  // Consent events
  'consent_granted',
  'consent_revoked',
  'consent_scope_changed',
  // Key lifecycle events
  'onboarding_completed',
]);

/**
 * Check if an event is critical (cannot be pruned)
 */
export function isCriticalEvent(eventName: EventName): boolean {
  return CRITICAL_EVENTS.has(eventName);
}

/**
 * Get event priority based on event name
 */
export function getEventPriority(eventName: EventName): EventPriority {
  if (CRITICAL_EVENTS.has(eventName)) {
    return 'critical';
  }

  // High priority events
  if (eventName.startsWith('crisis_') || eventName.startsWith('escalation_')) {
    return 'high';
  }

  // Normal priority (most events)
  if (
    eventName.includes('completed') ||
    eventName.includes('session') ||
    eventName.startsWith('ai_coach_')
  ) {
    return 'normal';
  }

  // Low priority
  return 'low';
}

